import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  Package,
  XCircle,
  DollarSign
} from 'lucide-react';

const StatsCards = ({ orders, setActiveTab, setServicesSubTab, canSeeClientPrices = true }) => {
  const { t } = useTranslation();

  const statsConfig = [
    {
      id: 'in-progress',
      label: 'Servicios en Curso',
      value: orders.filter(o => ['assigned', 'in_transit', 'confirmed'].includes(o.status)).length,
      icon: TrendingUp,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: false
    },
    {
      id: 'total-completed',
      label: 'Servicios Totales Hechos',
      value: orders.filter(o => o.approval_status === 'approved').length,
      icon: CheckCircle,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      onClick: () => {
        setActiveTab('orders');
        setServicesSubTab('historical');
      },
      requiresClientPrices: false
    },
    {
      id: 'pending-completion',
      label: 'Pendientes Cierre (Chóferes)',
      value: orders.filter(o => 
        o.status === 'completed' && 
        (!o.has_pickup_photos || !o.has_delivery_photos || !o.approval_status)
      ).length,
      icon: AlertTriangle,
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: false
    },
    {
      id: 'completed',
      label: 'Servicios Completados',
      value: orders.filter(o => o.status === 'completed').length,
      icon: Package,
      bgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: false
    },
    {
      id: 'pending-payment',
      label: 'Pendientes de Pago',
      value: orders.filter(o => o.payment_status !== 'paid' && o.payment_status !== 'cancelled').length,
      icon: XCircle,
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: true  // Only visible if can see client prices
    },
    {
      id: 'paid',
      label: 'Servicios Pagados',
      value: orders.filter(o => o.payment_status === 'paid').length,
      icon: DollarSign,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: true  // Only visible if can see client prices
    },
    {
      id: 'pending-approvals',
      label: 'Aprobaciones Pendientes',
      value: orders.filter(o => o.approval_status === 'pending_approval').length,
      icon: AlertTriangle,
      bgColor: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      onClick: () => setActiveTab('approvals'),
      requiresClientPrices: false
    },
    {
      id: 'total-revenue',
      label: 'Ingresos Totales',
      value: `€${orders.reduce((sum, o) => sum + (o.total_price || 0), 0).toFixed(2)}`,
      icon: DollarSign,
      bgColor: 'bg-[#36e961] bg-opacity-20',
      iconColor: 'text-[#36e961]',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: true  // Only visible if can see client prices
    }
  ];

  // Filter stats based on permissions
  const visibleStats = statsConfig.filter(stat => 
    !stat.requiresClientPrices || canSeeClientPrices
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {visibleStats.map((stat) => {
        const Icon = stat.icon;
        
        return (
          <div
            key={stat.id}
            className="stat-card cursor-pointer hover:shadow-lg transition-shadow"
            onClick={stat.onClick}
            data-testid={`stat-${stat.id}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">{stat.label}</p>
                <p className="stat-value">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                <Icon className={`w-6 h-6 ${stat.iconColor}`} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatsCards;
